/**
 * ListOnChannelSheet — Confirmation dialog for creating a new listing on a channel.
 *
 * Shows product preview (name, price, stock), category search (server-side filtered,
 * debounced 300ms), and a "Create Listing" button. Uses Dialog (not Sheet) per
 * existing codebase patterns.
 *
 * Plan 192, Epic 06.
 */

import { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChannelLogo } from "./ChannelLogo";
import { useChannelCategories, useCreateChannelListing } from "../api/listingsHooks";
import type { ChannelCategory } from "../api/listingsApi";
import { Loader2, Search, Check, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";

interface ListOnChannelSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: string;
  connectionName: string;
  itemCode: string;
  itemName: string;
  price: number;
  stock: number;
}

export function ListOnChannelSheet({
  open,
  onOpenChange,
  channel,
  connectionName,
  itemCode,
  itemName,
  price,
  stock,
}: ListOnChannelSheetProps) {
  const [categoryQuery, setCategoryQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<ChannelCategory | null>(null);
  const debouncedQuery = useDebounce(categoryQuery, 300);

  const { data: categories, isLoading: categoriesLoading } = useChannelCategories(
    channel,
    connectionName,
    debouncedQuery,
  );

  const createMutation = useCreateChannelListing();

  const handleCreate = useCallback(() => {
    if (!selectedCategory) return;
    createMutation.mutate(
      {
        itemCode,
        channel,
        connectionName,
        categoryId: selectedCategory.category_id,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setCategoryQuery("");
          setSelectedCategory(null);
        },
      },
    );
  }, [selectedCategory, itemCode, channel, connectionName, createMutation, onOpenChange]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setCategoryQuery("");
        setSelectedCategory(null);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const formattedPrice = useMemo(() => {
    return `฿${Number(price).toLocaleString("th-TH", { minimumFractionDigits: 0 })}`;
  }, [price]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <ChannelLogo channel={channel} size={20} />
            <span>List on {channel}</span>
          </DialogTitle>
          <DialogDescription>
            This will create a new listing on {channel} with:
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 space-y-4">
          {/* Product preview */}
          <div className="rounded-xl bg-muted/50 p-4 space-y-2">
            <PreviewRow label="Product" value={itemName} />
            <PreviewRow label="Price" value={formattedPrice} />
            <PreviewRow label="Stock" value={`${stock} units`} />
          </div>

          {/* Category search */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Category
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search categories..."
                value={categoryQuery}
                onChange={(e) => {
                  setCategoryQuery(e.target.value);
                  setSelectedCategory(null);
                }}
                className="pl-9"
              />
            </div>

            {/* Selected category display */}
            {selectedCategory && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20">
                <Check className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm truncate">{selectedCategory.name}</span>
                <button
                  type="button"
                  className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setSelectedCategory(null)}
                >
                  Change
                </button>
              </div>
            )}

            {/* Category results */}
            {!selectedCategory && (
              <CategoryResults
                categories={categories}
                isLoading={categoriesLoading}
                query={debouncedQuery}
                onSelect={setSelectedCategory}
              />
            )}
          </div>

          {/* Note about editing on channel */}
          <p className="text-xs text-muted-foreground">
            You can edit the listing on {channel} Seller Centre after it&apos;s created.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 space-y-3">
          {/* Error message */}
          {createMutation.isError && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{createMutation.error?.message || "Failed to create listing"}</span>
            </div>
          )}

          <Button
            className="w-full"
            disabled={!selectedCategory || createMutation.isPending}
            onClick={handleCreate}
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Listing"
            )}
          </Button>

          <button
            type="button"
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Internal sub-components ---

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium truncate ml-4 max-w-[60%] text-right">{value}</span>
    </div>
  );
}

function CategoryResults({
  categories,
  isLoading,
  query,
  onSelect,
}: {
  categories: ChannelCategory[] | undefined;
  isLoading: boolean;
  query: string;
  onSelect: (cat: ChannelCategory) => void;
}) {
  if (!query && !categories?.length) return null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading categories...</span>
      </div>
    );
  }

  if (!categories?.length) {
    return (
      <p className="text-sm text-muted-foreground text-center py-3">
        No categories found
      </p>
    );
  }

  return (
    <div className="max-h-48 overflow-y-auto rounded-lg border divide-y">
      {categories.map((cat) => (
        <button
          key={cat.category_id}
          type="button"
          className={cn(
            "flex items-center w-full text-left px-3 py-2.5 text-sm",
            "hover:bg-muted/50 transition-colors",
          )}
          onClick={() => onSelect(cat)}
        >
          <span className="truncate">{cat.name}</span>
          {cat.is_leaf && (
            <span className="ml-auto text-xs text-muted-foreground shrink-0">Leaf</span>
          )}
        </button>
      ))}
    </div>
  );
}
